/**
 * Shared theme constants — single source of truth for all semantic colors,
 * status badges, action indicators, and messaging colors.
 *
 * All pages/components should import from here instead of defining local colors.
 * Re-exports ADMIN_COLORS for backward compatibility.
 */

// ── Base Design Tokens ──────────────────────────────────────────────
export { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';
import { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';

// ── Status Badge Configs ────────────────────────────────────────────
// Used by: ReportsPage, PayrollPage, HospitalsPage, NotificationsPage

export const STATUS_BADGE = {
  // Report statuses
  DRAFT: { bg: ADMIN_COLORS.warningBg, color: ADMIN_COLORS.warningText, dot: ADMIN_COLORS.warning },
  SUBMITTED: { bg: ADMIN_COLORS.infoBg, color: ADMIN_COLORS.infoText, dot: 'var(--color-primary-500)' },
  APPROVED: { bg: ADMIN_COLORS.successBg, color: ADMIN_COLORS.successText, dot: ADMIN_COLORS.success },
  REJECTED: { bg: ADMIN_COLORS.dangerBg, color: ADMIN_COLORS.dangerText, dot: ADMIN_COLORS.signal },
  // Payroll statuses
  PAID: { bg: 'rgba(245, 158, 11, 0.12)', color: '#92400E', dot: '#F59E0B' },
  // Entity statuses
  ACTIVE: { bg: ADMIN_COLORS.successBg, color: ADMIN_COLORS.successText, dot: ADMIN_COLORS.success },
  INACTIVE: { bg: 'rgba(148, 163, 184, 0.12)', color: '#64748B', dot: '#94A3B8' },
  // General
  PENDING: { bg: ADMIN_COLORS.warningBg, color: ADMIN_COLORS.warningText, dot: ADMIN_COLORS.warning },
} as const;

// ── Action Colors ───────────────────────────────────────────────────
// Used by: AdminDashboardPage, ActivityLogPage

export const ACTION_COLORS: Record<string, string> = {
  // Auth actions
  LOGIN: 'var(--color-primary-500)',
  LOGOUT: '#64748B',
  // Report actions
  REPORT_CREATE: ADMIN_COLORS.forest,
  REPORT_SUBMIT: 'var(--color-primary-500)',
  REPORT_APPROVE: ADMIN_COLORS.success,
  REPORT_REJECT: ADMIN_COLORS.signal,
  REPORT_UPDATE: ADMIN_COLORS.leaf,
  // Admin actions
  ROLE_CHANGE: 'var(--color-accent-500)',
  USER_CREATE: ADMIN_COLORS.forest,
  USER_UPDATE: 'var(--color-primary-500)',
  USER_DELETE: ADMIN_COLORS.signal,
  USER_IMPERSONATE: 'var(--color-accent-500)',
  // Payroll
  PAYROLL_GENERATE: ADMIN_COLORS.warning,
  PAYROLL_APPROVE: ADMIN_COLORS.success,
  // Settings
  SETTINGS_UPDATE: '#64748B',
} as const;

// ── Notification Type Colors ────────────────────────────────────────
// Used by: NotificationsPage

export const NOTIFICATION_BADGE: Record<string, string> = {
  REPORT_SUBMITTED: 'bg-primary-100/50 text-primary-700',
  REPORT_APPROVED: 'bg-success-50/50 text-success-700',
  REPORT_REJECTED: 'bg-error-50/50 text-error-700',
  PAYROLL_GENERATED: 'bg-warning-50/50 text-warning-700',
  NEW_MESSAGE: 'bg-primary-100/50 text-primary-700',
  RSS_IMMEDIATE: 'bg-error-50/50 text-error-700',
  RSS_DAILY_DIGEST: 'bg-warning-50/50 text-warning-700',
  RSS_WEEKLY_DIGEST: 'bg-accent-50/50 text-accent-700',
  SYSTEM: 'bg-neutral-100/50 text-neutral-700',
} as const;

// ── Messaging Colors ────────────────────────────────────────────────
// Used by: MessageBubble, VoiceMessage, ChatPage, MessagesPage, etc.

export const MSG_COLORS = {
  // Bubble backgrounds
  sentBubble: 'var(--color-primary-50)',
  receivedBubble: 'rgba(255, 255, 255, 0.8)',
  // Audio waveform
  sentWaveform: 'var(--color-primary-300)',
  receivedWaveform: 'var(--color-neutral-300)',
  // Audio metadata text
  sentMetaText: 'var(--color-primary-700)',
  receivedMetaText: 'var(--color-neutral-500)',
  // Audio progress bar
  sentAudioBorder: 'var(--color-primary-400)',
  receivedAudioBorder: 'var(--color-neutral-300)',
  sentAudioBg: 'rgba(0, 0, 0, 0.05)',
  receivedAudioBg: 'rgba(255, 255, 255, 0.1)',
  sentAudioText: 'var(--color-primary-700)',
  receivedAudioText: 'rgba(255, 255, 255, 0.8)',
  // Online indicator
  online: 'var(--color-success-500, #0A9548)',
  // Borders & surfaces (glass-compatible)
  border: 'rgba(148, 163, 184, 0.3)',
  borderLight: 'rgba(148, 163, 184, 0.2)',
  surface: 'rgba(255, 255, 255, 0.45)',
  surfaceLight: 'rgba(255, 255, 255, 0.3)',
  // Status message tick
  tickGrey: 'var(--color-neutral-300)',
  tickBlue: 'var(--color-primary-800)',
} as const;

// ── File Type Colors ────────────────────────────────────────────────
// Used by: FileTypeIcon

export const FILE_TYPE_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  pdf: { text: 'text-error-600', bg: 'bg-error-50/50', border: 'border-error-300' },
  doc: { text: 'text-primary-600', bg: 'bg-primary-50/50', border: 'border-primary-300' },
  docx: { text: 'text-primary-600', bg: 'bg-primary-50/50', border: 'border-primary-300' },
  xls: { text: 'text-success-600', bg: 'bg-success-50/50', border: 'border-success-300' },
  xlsx: { text: 'text-success-600', bg: 'bg-success-50/50', border: 'border-success-300' },
  csv: { text: 'text-success-600', bg: 'bg-success-50/50', border: 'border-success-300' },
  ppt: { text: 'text-warning-600', bg: 'bg-warning-50/50', border: 'border-warning-300' },
  pptx: { text: 'text-warning-600', bg: 'bg-warning-50/50', border: 'border-warning-300' },
  zip: { text: 'text-accent-600', bg: 'bg-accent-50/50', border: 'border-accent-300' },
  rar: { text: 'text-accent-600', bg: 'bg-accent-50/50', border: 'border-accent-300' },
  default: { text: 'text-neutral-600', bg: 'bg-neutral-50/50', border: 'border-neutral-300' },
} as const;

// ── Auth Page Colors ────────────────────────────────────────────────
// Used by: LoginPage, RegisterPage

export const AUTH_COLORS = {
  warningBg: ADMIN_COLORS.warningBg,
  warningText: ADMIN_COLORS.warningText,
  errorBg: ADMIN_COLORS.dangerBg,
  errorText: ADMIN_COLORS.dangerText,
} as const;
